// frontend/src/pages/Pricing.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import LegalDocumentModal from "../components/legal/LegalDocumentModal";
import {
  Check,
  Zap,
  Building2,
  Crown,
  FileText,
  ArrowRight,
  ShieldCheck,
  Scale,
  Sparkles,
  RefreshCw,
  X,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import useEntitlement from "../hooks/useEntitlement";
import { resolveAuthenticatedHomePath } from "../lib/roleAwareRouting";

// Pricing is sourced from Supabase public.plans.
// Stripe remains authoritative for billing via stripe_price_id,
// and Supabase plans.price_usd_cents is authoritative for display.
// © 2026 Advanced Advisory Services, LLC. All Rights Reserved.

function badgeClasses() {
  return "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest border bg-white text-slate-700 border-slate-200";
}

function cardBorder(flag, isCurrent, isSelected) {
  if (isCurrent) return "border-emerald-500 shadow-lg";
  if (isSelected) return "border-blue-500 shadow-lg shadow-blue-100";
  if (flag === "Best for Lawyers & Consultants") return "border-amber-400 shadow-xl shadow-amber-100";
  return "border-slate-200 hover:border-slate-300 hover:shadow-md";
}

function moneyFromCents(cents) {
  const x = Number(cents);
  if (!Number.isFinite(x)) return "—";
  return (x / 100).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function parseNextFromLocation(location) {
  try {
    const qs = new URLSearchParams(location.search || "");
    const next = qs.get("next");
    return next ? String(next) : "";
  } catch {
    return "";
  }
}

function parseSelectedPlanFromLocation(location) {
  try {
    const qs = new URLSearchParams(location.search || "");
    const plan = qs.get("plan");
    return plan ? String(plan).trim() : "";
  } catch {
    return "";
  }
}

function normalizeFunctionsBaseUrl(value, supabaseUrl) {
  const primary = String(value || "").trim().replace(/\/$/, "");
  const fallback = String(supabaseUrl || "").trim().replace(/\/$/, "");
  const raw = primary || fallback;
  if (!raw) return "";

  try {
    const parsed = new URL(raw);

    if (/\.functions\.supabase\.co$/i.test(parsed.hostname)) {
      const projectRef = parsed.hostname.split(".")[0];
      return `https://${projectRef}.supabase.co/functions/v1`;
    }

    if (/\/functions\/v1$/i.test(parsed.pathname)) {
      return `${parsed.origin}${parsed.pathname}`;
    }

    return `${parsed.origin}${parsed.pathname}/functions/v1`.replace(/\/\/functions\/v1$/, "/functions/v1");
  } catch {
    return /\/functions\/v1$/i.test(raw) ? raw : `${raw}/functions/v1`;
  }
}

function buildFunctionsBaseCandidates(functionsBase, supabaseUrl) {
  const candidates = [
    normalizeFunctionsBaseUrl(functionsBase, supabaseUrl),
    normalizeFunctionsBaseUrl("", supabaseUrl),
    normalizeFunctionsBaseUrl(functionsBase, ""),
  ].filter(Boolean);

  return Array.from(new Set(candidates));
}

async function parseResponsePayload(res) {
  const fallback = {
    ok: false,
    json: null,
    text: "",
  };

  if (!res) return fallback;

  try {
    const text = await res.text();
    if (!text) {
      return {
        ok: true,
        json: null,
        text: "",
      };
    }

    try {
      return {
        ok: true,
        json: JSON.parse(text),
        text,
      };
    } catch {
      return {
        ok: true,
        json: null,
        text,
      };
    }
  } catch (error) {
    console.error("Failed to read checkout response body", error);
    return fallback;
  }
}

function uiForPlanKey(planKey) {
  const k = String(planKey || "").toLowerCase();

  if (k === "precision_pass") {
    return {
      icon: Zap,
      flag: null,
      note: null,
      intent: "paid",
      descriptionFallback: "One-time AI Risk Report for 1 business entity",
    };
  }

  if (k === "pro") {
    return {
      icon: FileText,
      flag: "Most Popular",
      note: null,
      intent: "paid",
      descriptionFallback: "Ongoing assessment plan for 1 business entity",
    };
  }

  if (k === "enterprise") {
    return {
      icon: Building2,
      flag: null,
      note: null,
      intent: "paid",
      descriptionFallback: "10 completed assessments per subscription billing period for 1 business entity",
    };
  }

  if (k === "global_pro") {
    return {
      icon: Crown,
      flag: "Best for Lawyers & Consultants",
      note: null,
      intent: "paid",
      descriptionFallback: "Multi-entity plan for lawyers, consultants, and advisory teams",
    };
  }

  return {
    icon: Sparkles,
    flag: null,
    note: null,
    intent: "paid",
    descriptionFallback: "Plan details",
  };
}

function labelForBillingType(billingType, period) {
  const bt = String(billingType || "").toLowerCase();
  if (bt === "one_time") return "One-Time";
  const p = String(period || "").toLowerCase();
  if (p === "year" || p === "annual" || p === "yearly") return "/year";
  return "/month";
}

function buildFeaturesFromPlanRow(plan) {
  const key = String(plan?.plan_key || "").toLowerCase();

  if (key === "precision_pass") {
    return [
      { text: "One-time AI risk assessment and report", included: true },
      { text: "For 1 business entity only", included: true },
      { text: "Downloadable AI Risk Report (PDF)", included: true },
      { text: "Quantified monetary risk exposure", included: true },
      { text: "Multiple jurisdictions available", included: true },
      { text: "Multi-currency risk exposure view", included: true },
      { text: "No ongoing monitoring or remediation features", included: false },
      { text: "No monthly subscription", included: false },
    ];
  }

  if (key === "pro") {
    return [
      { text: "2 completed full assessments per subscription billing period", included: true },
      { text: "For 1 business entity only", included: true },
      { text: "Snapshot report of current status", included: true },
      { text: "Remediation analysis and tracking", included: true },
      { text: "PDF export of reports", included: true },
      { text: "Shows monetary risk exposure", included: true },
      { text: "Multiple jurisdictions available", included: true },
      { text: "Multiple currency view available", included: true },
      { text: "Monthly subscription", included: true },
      { text: "Cancel any time", included: true },
    ];
  }

  if (key === "enterprise") {
    return [
      { text: "10 completed full assessments per subscription billing period", included: true },
      { text: "For 1 business entity only", included: true },
      { text: "Built for a single organization", included: true },
      { text: "Snapshot report of current status", included: true },
      { text: "Remediation analysis and tracking", included: true },
      { text: "PDF export of reports", included: true },
      { text: "Shows monetary risk exposure", included: true },
      { text: "Multiple jurisdictions available", included: true },
      { text: "Multiple currency view available", included: true },
      { text: "Monthly subscription", included: true },
      { text: "Cancel any time", included: true },
    ];
  }

  if (key === "global_pro") {
    return [
      { text: "Unlimited full audits per month", included: true },
      { text: "Run audits across multiple businesses", included: true },
      { text: "Multi-entity dashboard and management", included: true },
      { text: "Consultant and advisory workflows", included: true },
      { text: "Snapshot report of current status", included: true },
      { text: "Remediation analysis and tracking", included: true },
      { text: "PDF export of reports", included: true },
      { text: "Shows monetary risk exposure", included: true },
      { text: "Multiple jurisdictions available", included: true },
      { text: "Multiple currency view available", included: true },
      { text: "Monthly subscription", included: true },
      { text: "Cancel any time", included: true },
    ];
  }

  return [
    { text: "Snapshot report of status", included: true },
    { text: "Shows monetary risk exposure", included: true },
    { text: "Multiple jurisdictions available", included: true },
  ];
}

function CheckoutConsentModal({
  open,
  plan,
  accepted,
  submitting,
  onAcceptedChange,
  onCancel,
  onContinue,
  onReviewLegal,
}) {
  if (!open || !plan) return null;

  const billingLabel = labelForBillingType(plan.billing_type, plan.period);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 px-4 py-8 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-consent-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onCancel();
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.2em] text-amber-700">Final confirmation</div>
            <h2 id="checkout-consent-title" className="mt-2 text-2xl font-black text-slate-950">
              Review and continue to secure checkout
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Please review these documents before subscribing. By checking the box below, you confirm that you had the opportunity to review them and agree to be legally bound by the Subscriber Terms and Conditions before continuing to Stripe.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Close checkout confirmation"
            className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Selected plan</div>
            <div className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
              <div className="text-lg font-black text-slate-950">{plan.display_name}</div>
              <div className="text-lg font-black text-slate-950">
                ${moneyFromCents(plan.price_usd_cents)}
                <span className="ml-1 text-sm font-semibold text-slate-500">{billingLabel}</span>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => onReviewLegal("terms")}
              disabled={submitting}
              className="flex items-center justify-between rounded-2xl border-2 border-slate-200 bg-white px-4 py-4 text-left transition hover:border-blue-300 hover:bg-blue-50/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <FileText size={19} aria-hidden="true" />
                </span>
                <span className="block text-sm font-black text-slate-950">Terms and Conditions</span>
              </span>
              <span className="text-sm font-extrabold text-blue-700">Review</span>
            </button>

            <button
              type="button"
              onClick={() => onReviewLegal("legal")}
              disabled={submitting}
              className="flex items-center justify-between rounded-2xl border-2 border-slate-200 bg-white px-4 py-4 text-left transition hover:border-blue-300 hover:bg-blue-50/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <Scale size={19} aria-hidden="true" />
                </span>
                <span className="block text-sm font-black leading-tight text-slate-950">
                  <span className="block">Legal</span>
                  <span className="block">Disclaimer</span>
                </span>
              </span>
              <span className="text-sm font-extrabold text-blue-700">Review</span>
            </button>
          </div>

          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-slate-200 bg-white p-4 transition hover:border-slate-300">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => onAcceptedChange(event.target.checked)}
              disabled={submitting}
              className="mt-1 h-5 w-5 rounded border-slate-300 text-slate-950 focus:ring-slate-950"
            />
            <span className="text-sm leading-6 text-slate-700">
              I have had the opportunity to review and agree to be legally bound by the <strong className="font-extrabold text-slate-950">Subscriber Terms and Conditions (V4)</strong> and acknowledge the <strong className="font-extrabold text-slate-950">Legal Disclaimer</strong>.
            </span>
          </label>

          <div className="mt-3 rounded-xl bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-950">
            Your payment is processed securely by Stripe. AI Fine Protector does not store your payment-card details.
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-extrabold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={!accepted || submitting}
            className="rounded-xl bg-slate-950 px-5 py-3 text-sm font-extrabold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {submitting ? "Opening Secure Checkout…" : "Continue to Secure Stripe Checkout"}
          </button>
        </div>
      </div>
    </div>
  );
}

function buildPricingReturnPath(planKey, nextParam) {
  const params = new URLSearchParams();
  if (planKey) params.set("plan", String(planKey));
  if (nextParam) params.set("next", String(nextParam));
  const qs = params.toString();
  return `/pricing${qs ? `?${qs}` : ""}`;
}

function buildAuthPathForPlan({ plan, nextParam }) {
  const returnTo = buildPricingReturnPath(plan?.plan_key || "", nextParam);
  const params = new URLSearchParams();
  params.set("mode", "signup");
  params.set("next", returnTo);
  if (plan?.plan_key) params.set("plan", String(plan.plan_key));
  if (plan?.display_name) params.set("plan_name", String(plan.display_name));
  if (Number.isFinite(Number(plan?.price_usd_cents))) params.set("plan_price", String(plan.price_usd_cents));
  params.set("plan_billing", labelForBillingType(plan?.billing_type, plan?.period));
  return `/auth?${params.toString()}`;
}

export default function Pricing() {
  const navigate = useNavigate();
  const location = useLocation();

  const nextParam = useMemo(() => parseNextFromLocation(location), [location]);
  const selectedPlanKey = useMemo(() => parseSelectedPlanFromLocation(location), [location]);

  const [authed, setAuthed] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [pendingCheckoutPlan, setPendingCheckoutPlan] = useState(null);
  const [checkoutStarting, setCheckoutStarting] = useState(false);
  const [activeLegalDocument, setActiveLegalDocument] = useState(null);
  const [subscriptionRowState, setSubscriptionRowState] = useState({
    loading: false,
    planKey: "",
    status: "",
  });

  const [plansState, setPlansState] = useState({
    status: "loading",
    rows: [],
    error: null,
  });

  const {
    loading: entLoading,
    isPaid,
    rawPlanKey,
    paidStatus,
    refresh: refreshEntitlement,
  } = useEntitlement();

  const currentPlanKey = useMemo(() => {
    if (!isPaid) return "";
    return String(rawPlanKey || "");
  }, [isPaid, rawPlanKey]);

  const effectiveCurrentPlanKey = useMemo(() => {
    const entitlementKey = String(currentPlanKey || "").trim();
    if (entitlementKey) return entitlementKey;
    return String(subscriptionRowState.planKey || "").trim();
  }, [currentPlanKey, subscriptionRowState.planKey]);

  const effectiveIsPaid = useMemo(() => {
    if (isPaid) return true;
    return Boolean(effectiveCurrentPlanKey);
  }, [isPaid, effectiveCurrentPlanKey]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const ok = Boolean(data?.session?.access_token);
        if (mounted) setAuthed(ok);
      } catch {
        if (mounted) setAuthed(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const uid = session?.user?.id || "";
        if (!uid) {
          if (mounted) {
            setSubscriptionRowState({ loading: false, planKey: "", status: "" });
          }
          return;
        }

        if (mounted) {
          setSubscriptionRowState((prev) => ({ ...prev, loading: true }));
        }

        const { data, error } = await supabase
          .from("subscriptions")
          .select("plan_key, status, created_at")
          .eq("user_id", uid)
          .order("created_at", { ascending: false });

        if (!mounted) return;
        if (error) {
          console.warn("Pricing fallback subscription lookup failed", error);
          setSubscriptionRowState({ loading: false, planKey: "", status: "" });
          return;
        }

        const rows = Array.isArray(data) ? data : [];
        const activeLike = rows.find((row) => {
          const status = String(row?.status || "").toLowerCase();
          return ["active", "active_one_time", "trialing"].includes(status);
        });
        const chosen = activeLike || rows[0] || null;

        setSubscriptionRowState({
          loading: false,
          planKey: String(chosen?.plan_key || ""),
          status: String(chosen?.status || ""),
        });
      } catch (error) {
        if (!mounted) return;
        console.warn("Pricing fallback subscription lookup crashed", error);
        setSubscriptionRowState({ loading: false, planKey: "", status: "" });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [authed, isPaid, rawPlanKey, paidStatus]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setPlansState({ status: "loading", rows: [], error: null });

        const { data, error } = await supabase
          .from("plans")
          .select(
            "plan_key, display_name, billing_type, price_usd_cents, stripe_price_id, stripe_product_id, is_active, allow_reports, allow_remediation, max_businesses, audits_per_period, period, allow_consultant_workflow"
          )
          .order("price_usd_cents", { ascending: true });

        if (!mounted) return;

        if (error) {
          setPlansState({ status: "error", rows: [], error: error.message });
          return;
        }

        const rows = Array.isArray(data) ? data : [];
        const active = rows.filter((r) => r && r.is_active === true);

        setPlansState({ status: "ready", rows: active, error: null });
      } catch (e) {
        if (!mounted) return;
        setPlansState({
          status: "error",
          rows: [],
          error: String(e?.message || e || "Failed to load plans"),
        });
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const orderedPlans = useMemo(() => {
    const rows = plansState.rows || [];
    const order = ["precision_pass", "pro", "enterprise", "global_pro"];
    const byKey = new Map(rows.map((r) => [String(r.plan_key), r]));
    const out = [];

    for (const k of order) {
      if (byKey.has(k)) out.push(byKey.get(k));
    }

    for (const r of rows) {
      const k = String(r?.plan_key || "");
      if (!k) continue;
      if (!order.includes(k)) out.push(r);
    }

    return out.filter(Boolean);
  }, [plansState.rows]);

  const selectedPlanRow = useMemo(() => {
    if (!selectedPlanKey) return null;
    return orderedPlans.find((plan) => String(plan?.plan_key || "") === selectedPlanKey) || null;
  }, [orderedPlans, selectedPlanKey]);

  const defaultSubscribeKey = useMemo(() => {
    const rows = plansState.rows || [];
    const keys = new Set(rows.map((r) => String(r?.plan_key || "")));
    const current = String(effectiveCurrentPlanKey || "");

    if (selectedPlanKey && keys.has(selectedPlanKey)) {
      return selectedPlanKey;
    }

    if (!current) {
      return keys.has("pro") ? "pro" : (rows?.[0]?.plan_key || "");
    }

    if (current === "precision_pass") {
      if (keys.has("pro")) return "pro";
      if (keys.has("enterprise")) return "enterprise";
      if (keys.has("global_pro")) return "global_pro";
      return "";
    }

    if (current === "pro") {
      if (keys.has("enterprise")) return "enterprise";
      if (keys.has("global_pro")) return "global_pro";
      return "";
    }

    if (current === "enterprise") {
      if (keys.has("global_pro")) return "global_pro";
      return "";
    }

    return "";
  }, [effectiveCurrentPlanKey, plansState.rows, selectedPlanKey]);

  const currentPlanDisplayName = useMemo(() => {
    if (!effectiveCurrentPlanKey) return "";
    const match = (plansState.rows || []).find((p) => String(p?.plan_key || "") === effectiveCurrentPlanKey);
    return String(match?.display_name || effectiveCurrentPlanKey);
  }, [effectiveCurrentPlanKey, plansState.rows]);

  const heroCtaLabel = useMemo(() => {
    if (!defaultSubscribeKey) return "Your Current Plan Is Active";
    if (!authed) return "Create Account to Subscribe";
    if (selectedPlanRow && !effectiveIsPaid) return "Continue to Checkout";
    return "Subscribe Now";
  }, [authed, defaultSubscribeKey, effectiveIsPaid, selectedPlanRow]);

  function goUniversal() {
    navigate("/universal-audit");
  }

  async function goAuthenticatedHome() {
    const destination = await resolveAuthenticatedHomePath({
      client: supabase,
      fallbackPath: "/dashboard",
    });
    navigate(destination);
  }

  function handlePlanAction(planKey) {
    const safePlanKey = String(planKey || "").trim();
    if (!safePlanKey) return;

    const plan = orderedPlans.find((row) => String(row?.plan_key || "") === safePlanKey) || null;
    if (!plan) return;

    if (!authed) {
      navigate(buildAuthPathForPlan({ plan, nextParam }), { replace: false });
      return;
    }

    setAcceptedTerms(false);
    setPendingCheckoutPlan(plan);
  }

  function closeCheckoutConsent() {
    if (checkoutStarting) return;
    setActiveLegalDocument(null);
    setPendingCheckoutPlan(null);
    setAcceptedTerms(false);
  }

  async function confirmCheckoutConsent() {
    if (!pendingCheckoutPlan || !acceptedTerms || checkoutStarting) return;
    await startCheckout(String(pendingCheckoutPlan.plan_key || ""));
  }

  async function startCheckout(planKey) {
    setCheckoutStarting(true);

    try {
      const anonKey = String(process.env.REACT_APP_SUPABASE_ANON_KEY || "").trim();
      if (!anonKey) {
        window.alert(
          "Checkout is not configured.\n\nMissing env var:\nREACT_APP_SUPABASE_ANON_KEY\n\nThis is required to call Supabase Edge Functions."
        );
        return;
      }

      const supabaseUrl = String(process.env.REACT_APP_SUPABASE_URL || "").trim();
      const configuredFunctionsBase = String(process.env.REACT_APP_SUPABASE_FUNCTIONS_BASE || "").trim();
      const baseCandidates = buildFunctionsBaseCandidates(configuredFunctionsBase, supabaseUrl);

      if (!baseCandidates.length) {
        window.alert(
          "Checkout is not configured.\n\nMissing or invalid Supabase Functions base URL.\n\nPlease verify REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_FUNCTIONS_BASE in the deployed environment."
        );
        return;
      }

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session || !session.access_token) {
        console.error("No valid session found", { sessionError, session });
        const plan = orderedPlans.find((row) => String(row?.plan_key || "") === String(planKey)) || null;
        navigate(buildAuthPathForPlan({ plan, nextParam }), { replace: false });
        return;
      }

      let activeSession = session;
      const next_path = nextParam && nextParam.startsWith("/") ? nextParam : "/dashboard";

      async function callCreateCheckoutSession(accessToken, baseUrl) {
        const url = `${baseUrl}/create_checkout_session`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: anonKey,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ plan_key: String(planKey), next_path, legal_acceptance: true }),
        });

        const payload = await parseResponsePayload(res);
        return { res, payload, url };
      }

      let lastAttempt = null;

      for (const baseUrl of baseCandidates) {
        const attempt = await callCreateCheckoutSession(activeSession.access_token, baseUrl);
        lastAttempt = attempt;

        if (attempt.res.ok) {
          const checkoutUrl = attempt.payload?.json?.checkout_url;
          if (!checkoutUrl) {
            console.error("Missing checkout_url in response", {
              baseUrl,
              parsed: attempt.payload?.json,
              text: attempt.payload?.text,
            });
            window.alert("Checkout failed: missing checkout URL.");
            return;
          }

          window.location.href = checkoutUrl;
          return;
        }

        if (attempt.res.status !== 401) {
          break;
        }
      }

      if (lastAttempt?.res?.status === 401) {
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        const refreshedSession = refreshData?.session || null;

        if (refreshError || !refreshedSession?.access_token) {
          console.error("Checkout auth refresh failed", {
            refreshError,
            lastAttemptBody: lastAttempt?.payload?.json || lastAttempt?.payload?.text || null,
            attemptedUrls: baseCandidates,
          });

          const plan = orderedPlans.find((row) => String(row?.plan_key || "") === String(planKey)) || null;
          navigate(buildAuthPathForPlan({ plan, nextParam }), { replace: false });
          return;
        }

        activeSession = refreshedSession;

        for (const baseUrl of baseCandidates) {
          const retryAttempt = await callCreateCheckoutSession(activeSession.access_token, baseUrl);
          lastAttempt = retryAttempt;

          if (retryAttempt.res.ok) {
            const checkoutUrl = retryAttempt.payload?.json?.checkout_url;
            if (!checkoutUrl) {
              console.error("Missing checkout_url in response after auth refresh", {
                baseUrl,
                parsed: retryAttempt.payload?.json,
                text: retryAttempt.payload?.text,
              });
              window.alert("Checkout failed: missing checkout URL.");
              return;
            }

            window.location.href = checkoutUrl;
            return;
          }

          if (retryAttempt.res.status !== 401) {
            break;
          }
        }
      }

      const maybeMsg =
        lastAttempt?.payload?.json?.message ||
        lastAttempt?.payload?.json?.error ||
        lastAttempt?.payload?.json?.details ||
        lastAttempt?.payload?.text ||
        "Unknown error";

      console.error("create_checkout_session failed", {
        status: lastAttempt?.res?.status ?? null,
        statusText: lastAttempt?.res?.statusText ?? null,
        body: lastAttempt?.payload?.json,
        rawText: lastAttempt?.payload?.text || "",
        attemptedUrls: baseCandidates,
      });

      if (lastAttempt?.res?.status === 401) {
        const plan = orderedPlans.find((row) => String(row?.plan_key || "") === String(planKey)) || null;
        navigate(buildAuthPathForPlan({ plan, nextParam }), { replace: false });
        return;
      }

      window.alert(
        `Checkout failed.\n\nStatus: ${lastAttempt?.res?.status ?? "Unknown"}\nMessage: ${String(maybeMsg).slice(0, 300)}\n\nOpen DevTools Console for details.`
      );
    } catch (err) {
      console.error("Checkout error", err);
      window.alert("Checkout error. Check console for details.");
    } finally {
      setCheckoutStarting(false);
    }
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto px-8 md:px-12 py-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className={badgeClasses()}>Plans</span>

                {entLoading || subscriptionRowState.loading ? (
                  <span className="text-[11px] font-bold text-slate-500">Checking subscription…</span>
                ) : effectiveIsPaid ? (
                  <span className="text-[11px] font-bold text-emerald-700">
                    Active: <span className="font-black">{currentPlanDisplayName}</span>
                    {paidStatus ? (
                      <span className="text-slate-400 font-semibold"> ({String(paidStatus)})</span>
                    ) : subscriptionRowState.status ? (
                      <span className="text-slate-400 font-semibold"> ({String(subscriptionRowState.status)})</span>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-[11px] font-bold text-slate-500">No active subscription detected</span>
                )}

                <button
                  type="button"
                  onClick={() => refreshEntitlement()}
                  className="ml-1 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-extrabold text-slate-700 hover:bg-slate-50"
                  title="Refresh subscription status"
                >
                  <RefreshCw size={12} />
                  Refresh
                </button>
              </div>

              <h1 className="mt-4 text-3xl md:text-4xl font-black tracking-tight text-slate-900">
                Choose the right level of defensibility
              </h1>

              <p className="mt-2 text-sm text-slate-500 font-semibold">
                Start with a free Universal Audit, then unlock Legal Mode, exports, and remediation tracking.
              </p>

              {selectedPlanRow ? (
                <div className="mt-5 max-w-3xl rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-950 shadow-sm">
                  <div className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">Selected plan</div>
                  <div className="mt-2 font-bold">
                    {selectedPlanRow.display_name} — ${moneyFromCents(selectedPlanRow.price_usd_cents)}{labelForBillingType(selectedPlanRow.billing_type, selectedPlanRow.period)}
                  </div>
                  <div className="mt-1 text-blue-900/80">
                    {authed
                      ? "Review the legal terms below, then continue to checkout when you are ready."
                      : "Create your account or sign in first. You will then return here ready to continue with this plan."}
                  </div>
                </div>
              ) : null}

              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  onClick={goUniversal}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-extrabold text-white hover:bg-slate-800"
                >
                  Run Universal Audit
                  <ArrowRight size={16} />
                </button>

                <button
                  onClick={() => {
                    if (defaultSubscribeKey) handlePlanAction(defaultSubscribeKey);
                  }}
                  disabled={!defaultSubscribeKey}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-5 py-3 text-sm font-extrabold ${
                    defaultSubscribeKey
                      ? "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                      : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {heroCtaLabel}
                  <Scale size={16} />
                </button>
              </div>

              {nextParam ? (
                <div className="mt-2 text-[11px] text-slate-400">
                  After subscribing you’ll return to: <span className="font-mono">{nextParam}</span>
                </div>
              ) : null}

              {!authed && defaultSubscribeKey ? (
                <div className="mt-2 text-[11px] text-slate-400">You’ll create your account or sign in first, then return to your selected plan before checkout.</div>
              ) : null}
            </div>

            <button
              onClick={() => {
                if (authed) {
                  void goAuthenticatedHome();
                } else {
                  navigate("/");
                }
              }}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
            >
              {authed ? "Back to Dashboard" : "Back to Landing Page"}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                <ShieldCheck size={18} />
                Governance-first
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Designed for board and regulator defensibility — not just checklists.
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                <Sparkles size={18} />
                Multi-regime exposure
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Model simultaneous exposure across regimes without FX-summing in early-stage planning.
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                <Scale size={18} />
                Traceable methodology
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Legal Mode exposes the reasoning, factors, and citations underpinning outputs.
              </div>
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plansState.status === "error" ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-950 lg:col-span-4">
                <div className="font-black">Unable to load pricing plans.</div>
                <div className="mt-2 text-xs text-amber-900/80">
                  Error: {String(plansState.error || "Unknown error")}
                </div>
                <div className="mt-3 text-xs text-amber-900/80">You can still run the free Universal Audit.</div>
              </div>
            ) : plansState.status === "loading" ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 lg:col-span-4">
                Loading plans…
              </div>
            ) : orderedPlans.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 lg:col-span-4">
                No plans are available to display right now.
              </div>
            ) : (
              orderedPlans.map((plan) => {
                const ui = uiForPlanKey(plan.plan_key);
                const Icon = ui.icon;

                const isCurrent = effectiveCurrentPlanKey && String(plan.plan_key) === String(effectiveCurrentPlanKey);
                const isSelected = selectedPlanKey && String(plan.plan_key) === String(selectedPlanKey);
                const billingLabel = labelForBillingType(plan.billing_type, plan.period);
                const features = buildFeaturesFromPlanRow(plan);

                return (
                  <div
                    key={String(plan.plan_key)}
                    className={`relative flex h-full flex-col bg-white rounded-2xl border-2 p-6 transition-all duration-300 ${cardBorder(
                      ui.flag,
                      isCurrent,
                      isSelected
                    )}`}
                  >
                    {isSelected && !isCurrent ? (
                      <div className="absolute -top-3 left-5 rounded-full bg-blue-600 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-sm">
                        Selected
                      </div>
                    ) : null}

                    <div className="mb-6 pt-2 min-h-[84px]">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-5 h-5 text-slate-900" />
                        <h3 className="text-lg font-black text-slate-900">
                          {plan.display_name || ui.descriptionFallback}
                        </h3>
                      </div>
                      <p className="text-sm text-slate-500">{ui.descriptionFallback}</p>
                    </div>

                    <div className="mb-6 min-h-[72px]">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-extrabold text-slate-900">
                          ${moneyFromCents(plan.price_usd_cents)}
                        </span>
                        <span className="text-slate-500 text-sm">{billingLabel}</span>
                      </div>
                    </div>

                    <ul className="space-y-2 mb-6 min-h-[332px]">
                      {features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          {feature.included ? (
                            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <span className="mt-0.5 inline-block h-4 w-4 flex-shrink-0 text-center text-slate-300">—</span>
                          )}
                          <span className={feature.included ? "text-slate-700" : "text-slate-400"}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto space-y-2">
                      {isCurrent ? (
                        <div className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-center text-sm font-extrabold text-white">
                          Your Current Plan
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            if (!isCurrent) handlePlanAction(plan.plan_key);
                          }}
                          disabled={Boolean(isCurrent)}
                          className={`w-full rounded-xl px-4 py-3 text-sm font-extrabold transition-all ${
                            ui.flag === "Best for Lawyers & Consultants"
                              ? "bg-amber-500 text-white hover:bg-amber-600"
                              : "bg-slate-900 text-white hover:bg-slate-800"
                          }`}
                        >
                          {!authed
                            ? "Create Account to Subscribe"
                            : isSelected && !effectiveIsPaid
                            ? "Continue to Checkout"
                            : "Subscribe Now"}
                        </button>
                      )}

                      {!authed ? (
                        <div className="text-[11px] text-slate-400">
                          Create your account or sign in first, then continue with this plan.
                        </div>
                      ) : !isCurrent ? (
                        <div className="text-[11px] leading-4 text-slate-400">
                          Review your plan and accept the required legal terms in the confirmation step.
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-8 pb-10 text-center text-[11px] text-slate-400">
            <p className="text-slate-500">
              Questions about AI Fine Protector ™ or which plan is right for your organization?
            </p>
            <p className="mt-1">
              Contact us at{" "}
              <a href="mailto:info@aifineprotector.com" className="font-semibold text-blue-700 transition hover:text-blue-800 hover:underline">
                info@aifineprotector.com
              </a>
            </p>
            <p className="mt-2">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</p>
          </div>
        </div>

        <CheckoutConsentModal
          open={Boolean(pendingCheckoutPlan)}
          plan={pendingCheckoutPlan}
          accepted={acceptedTerms}
          submitting={checkoutStarting}
          onAcceptedChange={setAcceptedTerms}
          onCancel={closeCheckoutConsent}
          onContinue={confirmCheckoutConsent}
          onReviewLegal={setActiveLegalDocument}
        />

        <LegalDocumentModal
          documentKey={activeLegalDocument}
          onClose={() => setActiveLegalDocument(null)}
        />
      </main>
    </div>
  );
}
